// TopToolbar.jsx - No gap between text and categories
import { useState, useEffect, useRef } from 'react';
import {
  AppBar, Toolbar, Box, Chip, Paper, List, ListItem, ListItemText,
  CircularProgress, IconButton, InputBase, Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useNavigate } from 'react-router-dom';
import useLocationSearch from './useLocationSearch';

export default function TopToolbar({
  onLocationSelect,
  onClearLocation,
  onCategorySelect,
  workerTypes = [],
  selectedCategory = null,
}) {
  const navigate = useNavigate();
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showResults, setShowResults] = useState(false);
  const { results, loading } = useLocationSearch(searchTerm);
  const searchRef = useRef(null);
  const paperRef = useRef(null);

  // Handle location selection from search
  const handlePlaceSelected = (locationData) => {
    setSelectedLocation(locationData);
    setSearchTerm('');
    setShowResults(false);
    if (onLocationSelect) {
      onLocationSelect({
        lat: locationData.lat,
        lng: locationData.lng,
        name: locationData.label,
        address: locationData.label
      });
    }
  };

  // Handle clearing location
  const handleClearLocation = () => {
    setSelectedLocation(null);
    setSearchTerm('');
    setShowResults(false);
    if (onClearLocation) {
      onClearLocation();
    }
    if (onLocationSelect) {
      onLocationSelect(null);
    }
  };

  // Handle category selection
  const handleCategorySelect = (category) => {
    if (selectedCategory === category) {
      onCategorySelect?.(null);
    } else {
      onCategorySelect?.(category);
    }
  };

  // Click outside to close results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

 return (
  <AppBar position="fixed" elevation={0} sx={{
    bgcolor: '#EFE7DD',
    color: '#222',
    backdropFilter: 'blur(10px)',
    zIndex: 1200,
  }}>
    <Toolbar sx={{ 
      flexWrap: 'wrap', 
      py: 1.3,  // Reduced padding
      px: 1,    // Add horizontal padding
      flexDirection: 'column',
      alignItems: 'stretch',
      minHeight: 'auto',  // Remove default minHeight
    }}>
      {/* Search Box */}
      <Box sx={{ width: '100%', position: 'relative' }} ref={searchRef}>
        <Paper
          ref={paperRef}
          component="form"
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            borderRadius: '30px',
            px: 1,
            backgroundColor: '#f2f5f7',
            border: '1px solid black',
          }}
          onSubmit={(e) => e.preventDefault()}
        >
          <IconButton onClick={() => navigate(-1)} sx={{ color: 'black' }}>
            <ArrowBackIosNewIcon />
          </IconButton>

          <InputBase
            sx={{ ml: 1, flex: 1, fontSize: '14px' }}
            placeholder={selectedLocation ? selectedLocation.label : "Search location..."}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setShowResults(true);
            }}
            onFocus={() => setShowResults(true)}
          />

          {(searchTerm || selectedLocation) && (
            <IconButton 
              onClick={handleClearLocation}
              sx={{ color: 'black' }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
          
          <IconButton type="submit" sx={{ color: 'black' }}>
            <SearchIcon />
          </IconButton>
        </Paper>

        {/* Search Results Dropdown */}
        {showResults && searchTerm.length >= 3 && (
          <Paper
            elevation={6}
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 1300,
              mt: 0.5,
              borderRadius: '12px',
              overflow: 'hidden',
              maxHeight: 300,
              overflowY: 'auto'
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : results.length > 0 ? (
              <List dense sx={{ py: 0 }}>
                {results.map((result, idx) => (
                  <ListItem 
                    key={idx}
                    onClick={() => handlePlaceSelected(result)}
                    sx={{ 
                      cursor: 'pointer', 
                      py: 1.5,
                      '&:hover': { bgcolor: 'action.hover' } 
                    }}
                  >
                    <LocationOnIcon sx={{ mr: 2, color: 'primary.main', fontSize: 20 }} />
                    <ListItemText 
                      primary={result.label}
                      primaryTypographyProps={{ fontSize: '14px' }}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="body2">No locations found</Typography>
              </Box>
            )}
          </Paper>
        )}
      </Box>

      {/* Looking for Home Services Text */}
      <Typography sx={{ fontSize: '16px', color: '#000', mb: 1, mt: 0.5, lineHeight: 1 , ml: 0.8}}>
        Looking for Home Services
      </Typography>
      
      {/* Categories Section */}
      <Box sx={{ width: '100%', overflowX: 'auto', mt: 0 }}>
        <Box sx={{ 
          display: 'flex', 
          gap: '6px', 
          flexWrap: 'nowrap',
          minWidth: 'min-content',
          pb: 0.2,
        }}>
          {workerTypes.map((profession) => {
            const isActive = selectedCategory === profession;
            return (
              <Chip
                key={profession}
                label={profession}
                onClick={() => handleCategorySelect(profession)}
                clickable
                size="small"
                sx={{
                  flexShrink: 0,
                  borderRadius: '20px',
                  fontWeight: 'bold',
                  fontSize: '12px',
                  height: '30px',
                  bgcolor: isActive ? '#000' : 'transparent',
                  color: isActive ? '#fff' : '#000',
                  border: '1px solid black',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: isActive ? '#333' : '#e0e0e0',
                    transform: 'scale(1.02)',
                  },
                }}
              />
            );
          })}
        </Box>
      </Box>
    </Toolbar>
  </AppBar>
);
}